import React, { useEffect, useRef } from 'react';
import { IndicatorSparkInfo } from './types';

type Props = {
  info: IndicatorSparkInfo;
};
function SparkLine({ info }: Props) {
  const el = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!el.current) {
      return;
    }

    const canvas = el.current;
    const canvasRect = canvas.getBoundingClientRect();
    canvas.width = canvasRect.width;
    canvas.height = canvasRect.height;
    canvas.style.width = `${canvasRect.width}px`;
    canvas.style.height = `${canvasRect.height}px`;

    const maxValue = Math.max(...info.values);
    const tickLength = (canvas.width - 5) / (info.values.length - 1);
    const ticks: (number | null)[] = [];
    info.values.forEach(value => {
      if (value === null) {
        ticks.push(null);
      } else {
        const tickHeight = (value / maxValue) * canvas.height;
        ticks.push(tickHeight);
      }
    });

    const maxTick = Math.max(...ticks);
    const minTick = Math.min(...ticks);
    const offset = (canvas.height - (maxTick - minTick)) / 2;

    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 1;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = info.isNegative ? '#BC1C0E' : '#167922';
    ctx.translate(0.5, 0.5); // half the line width to straddle the pixels
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.beginPath();

    let x = 0;
    let y = 0;
    ticks.forEach((tick, idx) => {
      if (idx === 0) {
        x = 0;
        y = canvas.height - tick + offset;
        ctx.moveTo(x, y);
      } else {
        x = idx * tickLength;
        y = canvas.height - tick + offset;
        ctx.lineTo(x, y);
      }
    });

    ctx.arc(x, y, 1.5, 0, 2 * Math.PI);
    ctx.stroke();
    ctx.translate(-0.5, -0.5); // reset
  }, [info]);

  const { isNegative, startValue, endValue } = info;

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-lighter">{startValue}</span>
      <canvas ref={el} className="block relative h-6 flex-grow flex-shrink-0" />
      <span className={`text-sm ${isNegative ? 'text-ui-red' : 'text-ui-green'}`}>{endValue}</span>
    </div>
  );
}

export { SparkLine };
